import { FC, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { Role, useRolesQuery } from 'api/RolesAPI';
import _keyBy from 'lodash/keyBy';
import { keyBy } from 'utils/arrayHelpers';
import { RolesContext } from './RolesContext';
import { useMembersContext } from './MembersContext';
import { CombinedRole } from '../types';

export const RolesContextProvider: FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  const { data: rolesRaw = {}, isLoading: isLoadingRoles, refetch: refetchRoles } = useRolesQuery();
  const roles: Role[] = rolesRaw?.roles || [];

  const { projectMemberships } = useMembersContext();

  const [rolesByScope, setRolesByScope] = useState<{ [key: string]: Role[] }>({});
  const [combinedRoles, setCombinedRoles] = useState<CombinedRole[]>([]);

  const { roleId } = useParams<{ roleId: string }>();

  const currentRole = useMemo(
    () => combinedRoles?.find((role: CombinedRole) => role.id.toString() === roleId),
    [combinedRoles, roleId],
  );

  useEffect(() => {
    if (!isLoadingRoles) {
      setRolesByScope(() => keyBy(roles, (role) => role.scope));
    }
  }, [roles]);

  useEffect(() => {
    if (!isLoadingRoles && rolesByScope.Team) {
      const keyedTeamRolesByName = _keyBy(rolesByScope?.Team, (role) => role.name);

      const matchedProjectAndTeamRoles = rolesByScope?.Project?.filter(
        (projectRole) => !!keyedTeamRolesByName[projectRole.name],
      );

      const combined = matchedProjectAndTeamRoles.map((projectRole) => {
        const mergedPermissions = projectRole.permissions.concat(
          keyedTeamRolesByName[projectRole.name].permissions,
        );
        const membersList = projectMemberships[projectRole.id] || [];

        return {
          ...projectRole,
          scope: 'ProjectTeam',
          permissions: mergedPermissions,
          members: membersList,
        };
      });

      setCombinedRoles(combined);
    }
  }, [rolesByScope?.Project, rolesByScope?.Team, projectMemberships]);

  const rolesById = _keyBy(combinedRoles, (role) => role.id);

  return (
    <RolesContext.Provider
      value={{
        combinedRoles,
        currentRole,
        isLoadingRoles,
        refetchRoles,
        rolesById,
      }}
    >
      {children}
    </RolesContext.Provider>
  );
};
